import { useMemo, useState } from 'react'
import { type IntakeRate, RateIntake } from './RateIntake'

type SourceType = 'line' | 'partner' | 'spot'

interface CommercialRate {
  id: string
  sourceType: SourceType
  provider: string
  origin: string
  destination: string
  equipment: string
  amount: number
  currency: string
  validUntil: string
  allocationLeft: number | null
  allocationTotal: number | null
  creditLeft: number | null
  creditTotal: number | null
  status: 'ready' | 'expiring' | 'credit_watch'
}

const RATES: CommercialRate[] = [
  {
    id: 'msc-neu-40hc',
    sourceType: 'line',
    provider: 'MSC',
    origin: 'CNSHA',
    destination: 'NLRTM',
    equipment: '40HC',
    amount: 3140,
    currency: 'USD',
    validUntil: '2026-10-31',
    allocationLeft: 12,
    allocationTotal: 20,
    creditLeft: 42000,
    creditTotal: 80000,
    status: 'ready',
  },
  {
    id: 'maersk-uk-40hc',
    sourceType: 'line',
    provider: 'Maersk',
    origin: 'CNSZX',
    destination: 'GBFXT',
    equipment: '40HC',
    amount: 3025,
    currency: 'USD',
    validUntil: '2026-10-12',
    allocationLeft: 4,
    allocationTotal: 12,
    creditLeft: 18600,
    creditTotal: 60000,
    status: 'expiring',
  },
  {
    id: 'seabridge-ham-40fr',
    sourceType: 'partner',
    provider: 'SeaBridge GmbH',
    origin: 'INMUN',
    destination: 'DEHAM',
    equipment: "40' FR",
    amount: 11840,
    currency: 'USD',
    validUntil: '2026-10-18',
    allocationLeft: 2,
    allocationTotal: 4,
    creditLeft: 12800,
    creditTotal: 50000,
    status: 'ready',
  },
  {
    id: 'atlas-nyc-40hc',
    sourceType: 'partner',
    provider: 'Atlas Co-load',
    origin: 'CNNGB',
    destination: 'USNYC',
    equipment: '40HC',
    amount: 4070,
    currency: 'USD',
    validUntil: '2026-10-25',
    allocationLeft: 8,
    allocationTotal: 10,
    creditLeft: 7400,
    creditTotal: 45000,
    status: 'credit_watch',
  },
  {
    id: 'cosco-rdam-spot',
    sourceType: 'spot',
    provider: 'COSCO',
    origin: 'CNSHA',
    destination: 'NLRTM',
    equipment: '40HC',
    amount: 3060,
    currency: 'USD',
    validUntil: '2026-10-09',
    allocationLeft: null,
    allocationTotal: null,
    creditLeft: 38500,
    creditTotal: 70000,
    status: 'expiring',
  },
  {
    id: 'one-lax-spot',
    sourceType: 'spot',
    provider: 'ONE',
    origin: 'CNSHA',
    destination: 'USLAX',
    equipment: '40HC',
    amount: 2590,
    currency: 'USD',
    validUntil: '2026-10-10',
    allocationLeft: null,
    allocationTotal: null,
    creditLeft: 28600,
    creditTotal: 55000,
    status: 'expiring',
  },
]

const SOURCE: Record<SourceType, { label: string; detail: string; tone: string }> = {
  line: { label: 'Line contract', detail: 'Direct carrier agreement', tone: '#2563eb' },
  partner: { label: 'Partner rate', detail: 'Agent or co-loader tariff', tone: '#8b5cf6' },
  spot: { label: 'Spot offer', detail: 'Live, short-validity quote', tone: '#e5556b' },
}

const money = (amount: number, currency = 'USD') =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency, maximumFractionDigits: 0 }).format(
    amount
  )

const percent = (left: number | null, total: number | null) =>
  left != null && total ? Math.max(0, Math.min(100, Math.round((left / total) * 100))) : null

export function RateBook() {
  const [rates, setRates] = useState(RATES)
  const [filter, setFilter] = useState<'all' | SourceType>('all')
  const [query, setQuery] = useState('')
  const visible = useMemo(() => {
    const q = query.trim().toLowerCase()
    return rates
      .filter((r) => filter === 'all' || r.sourceType === filter)
      .filter(
        (r) =>
          !q ||
          `${r.provider} ${r.origin} ${r.destination} ${r.equipment}`.toLowerCase().includes(q)
      )
  }, [filter, query, rates])

  const totalCredit = rates.reduce((sum, rate) => sum + (rate.creditLeft ?? 0), 0)
  const totalAllocation = rates.reduce((sum, rate) => sum + (rate.allocationLeft ?? 0), 0)
  const allocationCapacity = rates.reduce((sum, rate) => sum + (rate.allocationTotal ?? 0), 0)
  const addRate = (rate: IntakeRate) =>
    setRates((current) => [
      {
        ...rate,
        id: `manual-${Date.now()}`,
        allocationLeft: null,
        allocationTotal: null,
        creditLeft: null,
        creditTotal: null,
        status: 'ready',
      },
      ...current,
    ])

  return (
    <section className="frb" aria-label="My commercial rates">
      <style>{styles}</style>
      <header className="frb-intro">
        <div>
          <span>Commercial rate book</span>
          <h2>Your buying power, in one place.</h2>
          <p>Only rates you can use. Market benchmarks stay separate.</p>
        </div>
        <button
          type="button"
          onClick={() => document.querySelector('.rti')?.scrollIntoView({ behavior: 'smooth' })}
        >
          ＋ Add rate
        </button>
      </header>

      <RateIntake onAdd={addRate} />

      <div className="frb-position">
        <div className="frb-position-lead">
          <span>Available buying credit</span>
          <strong>{money(totalCredit)}</strong>
          <small>across 6 active carrier and partner accounts</small>
        </div>
        <div className="frb-meter-block">
          <div>
            <span>Committed allocation</span>
            <b>
              {totalAllocation} of {allocationCapacity} TEU left
            </b>
          </div>
          <div className="frb-meter">
            <i style={{ width: `${percent(totalAllocation, allocationCapacity)}%` }} />
          </div>
          <small>Space is indicative until the provider confirms the booking.</small>
        </div>
        <div className="frb-attention">
          <span>Needs attention</span>
          <b>
            <i>3</i> rates expire this week
          </b>
          <b>
            <i>1</i> partner near credit limit
          </b>
        </div>
      </div>

      <div className="frb-tools">
        <div className="frb-filters" aria-label="Rate source filter">
          {(['all', 'line', 'partner', 'spot'] as const).map((key) => (
            <button
              type="button"
              key={key}
              className={filter === key ? 'active' : ''}
              onClick={() => setFilter(key)}
            >
              {key === 'all' ? 'All usable rates' : SOURCE[key].label}
              <span>
                {key === 'all' ? rates.length : rates.filter((r) => r.sourceType === key).length}
              </span>
            </button>
          ))}
        </div>
        <label className="frb-search">
          <span>⌕</span>
          <input
            aria-label="Search rates"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search lane or provider"
          />
        </label>
      </div>

      <div className="frb-table-wrap">
        <table>
          <thead>
            <tr>
              <th>Source</th>
              <th>Lane</th>
              <th>Equipment</th>
              <th>Buy rate</th>
              <th>Valid</th>
              <th>Buying position</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {visible.map((rate) => (
              <RateRow key={rate.id} rate={rate} />
            ))}
          </tbody>
        </table>
        {!visible.length && <div className="frb-empty">No usable rates match this search.</div>}
      </div>

      <div className="frb-mobile-list">
        {visible.map((rate) => {
          const source = SOURCE[rate.sourceType]
          return (
            <article key={rate.id}>
              <header>
                <div className="frb-provider">
                  <i style={{ background: source.tone }} />
                  <span>
                    <b>{rate.provider}</b>
                    <small>{source.label}</small>
                  </span>
                </div>
                <strong>{money(rate.amount, rate.currency)}</strong>
              </header>
              <div className="frb-mobile-lane">
                <b>
                  {rate.origin} <i>→</i> {rate.destination}
                </b>
                <span>{rate.equipment}</span>
              </div>
              <div className="frb-mobile-position">
                <span className={rate.status === 'expiring' ? 'warn' : ''}>
                  {rate.status === 'expiring' ? 'Expires' : 'Valid to'}
                  <b>
                    {new Date(`${rate.validUntil}T00:00:00Z`).toLocaleDateString('en-GB', {
                      day: 'numeric',
                      month: 'short',
                      timeZone: 'UTC',
                    })}
                  </b>
                </span>
                {rate.allocationLeft != null && (
                  <span>
                    Allocation <b>{rate.allocationLeft} TEU</b>
                  </span>
                )}
                <span className={rate.status === 'credit_watch' ? 'warn' : ''}>
                  Credit <b>{money(rate.creditLeft ?? 0)}</b>
                </span>
              </div>
              <button type="button">Use this rate →</button>
            </article>
          )
        })}
      </div>

      <footer className="frb-legend">
        {(Object.keys(SOURCE) as SourceType[]).map((key) => (
          <div key={key}>
            <i style={{ background: SOURCE[key].tone }} />
            <span>
              <b>{SOURCE[key].label}</b>
              {SOURCE[key].detail}
            </span>
          </div>
        ))}
      </footer>
    </section>
  )
}

function RateRow({ rate }: { rate: CommercialRate }) {
  const source = SOURCE[rate.sourceType]
  const creditPct = percent(rate.creditLeft, rate.creditTotal)
  return (
    <tr>
      <td>
        <div className="frb-provider">
          <i style={{ background: source.tone }} />
          <span>
            <b>{rate.provider}</b>
            <small>{source.label}</small>
          </span>
        </div>
      </td>
      <td>
        <b className="frb-lane">
          {rate.origin} <i>→</i> {rate.destination}
        </b>
      </td>
      <td>
        <span className="frb-equip">{rate.equipment}</span>
      </td>
      <td>
        <strong className="frb-price">{money(rate.amount, rate.currency)}</strong>
      </td>
      <td>
        <span className={`frb-valid ${rate.status === 'expiring' ? 'warn' : ''}`}>
          {rate.status === 'expiring' ? 'Expires ' : 'Until '}
          {new Date(`${rate.validUntil}T00:00:00Z`).toLocaleDateString('en-GB', {
            day: 'numeric',
            month: 'short',
            timeZone: 'UTC',
          })}
        </span>
      </td>
      <td>
        <div className="frb-buying">
          {rate.allocationLeft != null && (
            <span>
              <b>{rate.allocationLeft} TEU</b> allocation
            </span>
          )}
          <span className={rate.status === 'credit_watch' ? 'warn' : ''}>
            <b>{money(rate.creditLeft ?? 0)}</b> credit
          </span>
          {creditPct != null && (
            <div>
              <i style={{ width: `${creditPct}%` }} />
            </div>
          )}
        </div>
      </td>
      <td>
        <button type="button" className="frb-use">
          Use →
        </button>
      </td>
    </tr>
  )
}

const styles = `
.frb{padding-top:24px}.frb *{box-sizing:border-box}.frb button,.frb input{font:inherit}.frb-intro{display:flex;align-items:flex-end;justify-content:space-between;gap:20px}.frb-intro>div>span{font-family:var(--mono,ui-monospace,monospace);font-size:9px;letter-spacing:.14em;text-transform:uppercase;color:var(--frd-accent);font-weight:700}.frb-intro h2{font-family:var(--font-display,Ranade,Georgia,serif);font-size:26px;letter-spacing:-.025em;margin:6px 0 3px}.frb-intro p{margin:0;color:var(--text-muted,#788392);font-size:13px}.frb-intro>button{border:1px solid var(--border,#e7e7e2);background:var(--surface-0,#fff);color:inherit;border-radius:10px;padding:9px 13px;font-size:12px;font-weight:700;cursor:pointer}.frb-position{display:grid;grid-template-columns:1.1fr 1.35fr 1fr;background:var(--text-primary,#101217);color:var(--surface-0,#fff);border-radius:16px;margin-top:18px;overflow:hidden;box-shadow:0 18px 42px -30px #000}.frb-position>div{padding:20px 22px;border-right:1px solid #ffffff18}.frb-position>div:last-child{border:0}.frb-position span,.frb-position small{display:block;color:#aeb7c4;font-size:10px}.frb-position-lead strong{display:block;font-family:var(--mono,ui-monospace,monospace);font-size:29px;margin:7px 0 3px;letter-spacing:-.035em}.frb-meter-block>div:first-child{display:flex;justify-content:space-between;gap:10px}.frb-meter-block b{font-family:var(--mono,ui-monospace,monospace);font-size:12px}.frb-meter{height:6px;background:#ffffff16;border-radius:10px;overflow:hidden;margin:13px 0 8px}.frb-meter i{display:block;height:100%;background:var(--frd-accent);border-radius:10px}.frb-attention>b{display:flex;gap:9px;align-items:center;font-size:11px;margin-top:9px}.frb-attention b i{font-family:var(--mono,ui-monospace,monospace);font-style:normal;width:20px;height:20px;border-radius:6px;background:#e5556b2a;color:#ff91a1;display:grid;place-items:center}.frb-tools{display:flex;justify-content:space-between;gap:16px;align-items:center;margin-top:18px}.frb-filters{display:flex;gap:6px;flex-wrap:wrap}.frb-filters button{border:1px solid var(--border,#e7e7e2);background:var(--surface-0,#fff);color:var(--text-muted,#788392);border-radius:9px;padding:7px 9px;font-size:11px;cursor:pointer}.frb-filters button.active{color:var(--text-primary,#111);border-color:color-mix(in srgb,var(--frd-accent) 50%,var(--border));background:color-mix(in srgb,var(--frd-accent) 6%,var(--surface-0,#fff))}.frb-filters span{font-family:var(--mono,ui-monospace,monospace);font-size:9px;margin-left:5px;opacity:.65}.frb-search{display:flex;align-items:center;gap:5px;border:1px solid var(--border,#e7e7e2);background:var(--surface-0,#fff);border-radius:9px;padding:0 9px;min-width:220px}.frb-search span{color:var(--text-muted)}.frb-search input{border:0;outline:0;background:transparent;color:inherit;width:100%;padding:8px 3px;font-size:11px}.frb-table-wrap{border:1px solid var(--border,#e7e7e2);background:var(--surface-0,#fff);border-radius:15px;overflow:auto;margin-top:10px;box-shadow:var(--shadow-card,none)}.frb table{border-collapse:collapse;width:100%;min-width:900px}.frb th{text-align:left;font-family:var(--mono,ui-monospace,monospace);font-size:8.5px;letter-spacing:.09em;text-transform:uppercase;color:var(--text-muted,#788392);padding:10px 13px;border-bottom:1px solid var(--border,#e7e7e2)}.frb td{padding:12px 13px;border-bottom:1px solid var(--border,#e7e7e2);font-size:11px;vertical-align:middle}.frb tr:last-child td{border-bottom:0}.frb-provider{display:flex;align-items:center;gap:9px}.frb-provider>i{width:3px;height:30px;border-radius:5px}.frb-provider span b,.frb-provider span small{display:block}.frb-provider span small{font-size:9px;color:var(--text-muted);margin-top:2px}.frb-lane{font-family:var(--mono,ui-monospace,monospace);white-space:nowrap}.frb-lane i{font-style:normal;color:var(--frd-accent);padding:0 3px}.frb-equip{font-family:var(--mono,ui-monospace,monospace);background:var(--surface-1,#f4f4f1);padding:4px 6px;border-radius:6px;white-space:nowrap}.frb-price{font-family:var(--mono,ui-monospace,monospace);font-size:13px}.frb-valid{white-space:nowrap;color:var(--text-muted)}.frb-valid.warn,.frb-buying .warn{color:#b45309}.frb-buying{min-width:125px}.frb-buying>span{display:block;color:var(--text-muted);font-size:9.5px;line-height:1.5}.frb-buying>span b{color:inherit;font-family:var(--mono,ui-monospace,monospace)}.frb-buying>div{height:3px;background:var(--surface-2,#e9e9e4);border-radius:5px;margin-top:4px;overflow:hidden}.frb-buying>div i{display:block;height:100%;background:#10b981;border-radius:5px}.frb-use{border:0;background:none;color:var(--frd-accent);font-size:10px;font-weight:750;white-space:nowrap;cursor:pointer}.frb-empty{padding:28px;text-align:center;color:var(--text-muted);font-size:12px}.frb-legend{display:flex;gap:22px;margin-top:13px;flex-wrap:wrap}.frb-legend>div{display:flex;gap:7px;align-items:flex-start}.frb-legend i{width:3px;height:24px;border-radius:4px}.frb-legend span{display:grid;color:var(--text-muted);font-size:9px}.frb-legend b{color:var(--text-primary);font-size:10px;margin-bottom:1px}
.frb-mobile-list{display:none}
@media(max-width:760px){.frb-intro{align-items:flex-start}.frb-intro>button{white-space:nowrap}.frb-position{grid-template-columns:1fr}.frb-position>div{border-right:0;border-bottom:1px solid #ffffff18}.frb-tools{align-items:stretch;flex-direction:column}.frb-search{width:100%}.frb-table-wrap{display:none}.frb-mobile-list{display:grid;gap:9px;margin-top:10px}.frb-mobile-list article{border:1px solid var(--border,#e7e7e2);background:var(--surface-0,#fff);border-radius:12px;padding:14px;box-shadow:var(--shadow-card,none)}.frb-mobile-list article header{display:flex;justify-content:space-between;align-items:center}.frb-mobile-list article header>strong{font-family:var(--mono,ui-monospace,monospace);font-size:16px}.frb-mobile-lane{display:flex;justify-content:space-between;align-items:center;margin-top:13px;padding-top:11px;border-top:1px solid var(--border,#e7e7e2)}.frb-mobile-lane b{font-family:var(--mono,ui-monospace,monospace);font-size:11px}.frb-mobile-lane b i{font-style:normal;color:var(--frd-accent);padding:0 3px}.frb-mobile-lane>span{font-family:var(--mono,ui-monospace,monospace);font-size:10px;background:var(--surface-1,#f4f4f1);padding:4px 6px;border-radius:5px}.frb-mobile-position{display:grid;grid-template-columns:repeat(3,1fr);gap:7px;margin-top:11px}.frb-mobile-position span{font-size:8px;color:var(--text-muted);text-transform:uppercase;letter-spacing:.04em}.frb-mobile-position span b{display:block;font-family:var(--mono,ui-monospace,monospace);font-size:10px;color:var(--text-primary);text-transform:none;margin-top:3px}.frb-mobile-position span.warn,.frb-mobile-position span.warn b{color:#b45309}.frb-mobile-list article>button{width:100%;border:0;background:var(--text-primary,#111);color:var(--surface-0,#fff);border-radius:8px;padding:8px;margin-top:12px;font-size:10px;font-weight:750}.frb-legend{display:none}}
`
